import { type EntityId, type SceneDocument } from '@/domain/model';
import { evaluateFrom } from '@/engine';

import { type EditAction } from './actions';
import { applyEdit, createEditorState, type EditOptions, type EditorState } from './EditorState';
import {
  canRedo,
  canUndo,
  type Command,
  createHistory,
  // Imported by name deliberately: without it, `History` silently resolves to the DOM's
  // global `History` interface and every use here type-checks against the wrong thing.
  type History,
  record,
  redo,
  seal,
  undo,
} from './History';
import { applyPatch, diffDocuments, type DocumentPatch } from './patch';

/**
 * Editor state plus its undo history.
 *
 * Every edit goes through `execute`, which runs the full pipeline and records what
 * actually happened as a reversible patch. Nothing else in the application may mutate
 * the document, which is what makes "is this undoable?" a question with one answer
 * rather than a per-feature audit.
 */

export interface EditorSession {
  readonly state: EditorState;
  readonly history: History;
}

export interface SessionResult {
  readonly session: EditorSession;
  /** Entities whose evaluated value changed — what the renderer must repaint. */
  readonly changed: ReadonlySet<EntityId>;
}

export function createSession(document: SceneDocument, historyLimit?: number): EditorSession {
  return {
    state: createEditorState(document),
    history: createHistory(historyLimit),
  };
}

export interface ExecuteOptions extends EditOptions {
  /** Shown in the undo menu. */
  readonly label?: string;
  /**
   * Gesture identity. Consecutive edits sharing a key compress into one undo step; see
   * `History.record`.
   */
  readonly mergeKey?: string | undefined;
  /** Skip history entirely — for previews that must not be undoable. */
  readonly transient?: boolean;
}

export function execute(
  session: EditorSession,
  actions: readonly EditAction[],
  options: ExecuteOptions = {}
): SessionResult {
  const before = session.state.document;
  const result = applyEdit(session.state, actions, options);

  if (options.transient === true) {
    return { session: { ...session, state: result.state }, changed: result.changed };
  }

  const patch = diffDocuments(before, result.state.document, result.touched);
  const command: Command = {
    label: options.label ?? 'Edit',
    patch,
    mergeKey: options.mergeKey,
  };

  return {
    session: { state: result.state, history: record(session.history, command) },
    changed: result.changed,
  };
}

/** Ends the current gesture, so the next edit starts a fresh undo step. */
export function sealSession(session: EditorSession): EditorSession {
  const history = seal(session.history);
  return history === session.history ? session : { ...session, history };
}

export function canUndoSession(session: EditorSession): boolean {
  return canUndo(session.history);
}

export function canRedoSession(session: EditorSession): boolean {
  return canRedo(session.history);
}

export function undoSession(session: EditorSession): SessionResult | null {
  const step = undo(session.history);
  if (step === null) return null;

  const applied = applyHistoryPatch(session, step.patch);
  return {
    session: { state: applied.state, history: step.history },
    changed: applied.changed,
  };
}

export function redoSession(session: EditorSession): SessionResult | null {
  const step = redo(session.history);
  if (step === null) return null;

  const applied = applyHistoryPatch(session, step.patch);
  return {
    session: { state: applied.state, history: step.history },
    changed: applied.changed,
  };
}

/* ------------------------------------------------------------------- internals */

/**
 * Applies a history patch and brings the scene up to date.
 *
 * Runs **Tier 1 only** — no constraint solving. The patch already holds the document as
 * it was *after* the solver ran, so re-solving would be at best a no-op and at worst a
 * second, slightly different solution: an under-constrained cluster has many valid
 * configurations, and there is no guarantee the solver returns to the one the user saw.
 * Undo has to be exact, not merely valid.
 */
function applyHistoryPatch(
  session: EditorSession,
  patch: DocumentPatch
): { readonly state: EditorState; readonly changed: ReadonlySet<EntityId> } {
  const document = applyPatch(session.state.document, patch);
  const seeds = patch.entities.map((change) => change.id);

  const evaluation = evaluateFrom(session.state.scene, document, seeds);

  const changed = new Set(evaluation.changed);
  for (const id of seeds) changed.add(id);

  // A restored deletion must not leave the vanished ids selected.
  let selection = session.state.selection;
  if (selection.size > 0) {
    const next = new Set<EntityId>();
    for (const id of selection) {
      if (document.entities.has(id)) next.add(id);
    }
    if (next.size !== selection.size) selection = next;
  }

  return {
    state: { document, scene: evaluation.scene, selection },
    changed,
  };
}
